import type { AddMemberInput, WardMemberRoleInput } from "@handoverpro/shared";

import {
  addWardMember as sdkAddWardMember,
  listWardMembers as sdkListWardMembers,
  removeWardMember as sdkRemoveWardMember,
  updateWardMemberRole as sdkUpdateWardMemberRole,
  type WardMemberDto,
} from "~/lib/api";

/**
 * Network layer for the ward-roster screen (V2-E2.2, HAN-87). Wraps the
 * generated SDK calls (HAN-84's `/wards/:wardId/members`) in discriminated-union
 * results so the screen stays free of HTTP-status details — mirrors `~/lib/wards`.
 * The whole `/members` controller is `@RequireWardAdmin`, so every call here is
 * only ever made by a ward admin or super-admin.
 */

export type FetchResult<T> = { status: "ok"; data: T } | { status: "error"; message: string };

export type MemberResult =
  | { status: "ok"; member: WardMemberDto }
  | { status: "error"; message: string };

export type RemoveResult = { status: "ok" } | { status: "error"; message: string };

export async function fetchWardMembers(wardId: string): Promise<FetchResult<WardMemberDto[]>> {
  const result = await sdkListWardMembers({ path: { wardId } });
  if (result.error || !result.data) {
    return { status: "error", message: `Couldn't load the roster (${result.response.status}).` };
  }
  return { status: "ok", data: result.data };
}

/**
 * Add an already-registered user to the ward. A 404 means no registered user
 * matches — surfaced as a distinct message so the screen can point the admin at
 * the email-invite path (E3.3) instead of looking like a transient failure.
 */
export async function addWardMember(wardId: string, input: AddMemberInput): Promise<MemberResult> {
  const result = await sdkAddWardMember({ path: { wardId }, body: input });
  if (result.error || !result.data) {
    return { status: "error", message: messageForAddStatus(result.response.status) };
  }
  return { status: "ok", member: result.data };
}

function messageForAddStatus(status: number): string {
  switch (status) {
    case 400:
      return "Enter a valid email address or a 7-digit GMC number.";
    case 403:
      return "Only a ward admin can manage the roster.";
    case 404:
      return "No registered user matches that email or GMC number.";
    default:
      return "Something went wrong adding the member. Please try again.";
  }
}

export async function updateWardMemberRole(
  wardId: string,
  userId: string,
  role: WardMemberRoleInput,
): Promise<MemberResult> {
  const result = await sdkUpdateWardMemberRole({ path: { wardId, userId }, body: { role } });
  if (result.error || !result.data) {
    return { status: "error", message: messageForRoleOrRemoveStatus(result.response.status) };
  }
  return { status: "ok", member: result.data };
}

/**
 * Remove a member. A 409 means they're the ward's last remaining admin — the API
 * keeps every ward administrable, so this is reported, not retried.
 */
export async function removeWardMember(wardId: string, userId: string): Promise<RemoveResult> {
  const result = await sdkRemoveWardMember({ path: { wardId, userId } });
  // 204 No Content: the SDK leaves `data` undefined, so success is "no error".
  if (result.error) {
    return { status: "error", message: messageForRoleOrRemoveStatus(result.response.status) };
  }
  return { status: "ok" };
}

function messageForRoleOrRemoveStatus(status: number): string {
  switch (status) {
    case 403:
      return "Only a ward admin can manage the roster.";
    case 404:
      return "That member is no longer on this ward.";
    case 409:
      return "This ward needs at least one admin — promote another member first.";
    default:
      return "Something went wrong updating the roster. Please try again.";
  }
}
